import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(description: string, lineSpacing: number, bScale: number) {
  if (!description) return null;
  const lines = description.split('\n').map((line) => line.trim()).filter(Boolean);
  const style = { fontSize: `${0.6875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="mt-1 whitespace-pre-line text-stone-600" style={style}>{description}</p>;
  }
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-stone-600 marker:text-amber-700" style={style}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*\s]+/, '')}</li>)}
    </ul>
  );
}

export function CreativeTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="min-h-full bg-orange-50 text-stone-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="grid grid-cols-[62%_38%]">
        <div className="bg-amber-700 px-9 py-8 text-orange-50">
          {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mb-3 border-2 border-orange-50" />}
          <p className="font-bold uppercase tracking-[0.35em] text-amber-100" style={{ fontSize: `${0.6 * bScale}rem` }}>Curriculum Vitae</p>
          <h1 className="mt-2 font-bold leading-none tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${2.25 * hScale}rem` }}>{personal.fullName || 'Your Name'}</h1>
        </div>
        <div className="bg-orange-200 px-6 py-7 text-stone-700" style={{ fontSize: `${0.6 * bScale}rem`, lineHeight: 1.6 }}>
          {personal.email && <p>{personal.email}</p>}
          {personal.phone && <p>{personal.phone}</p>}
          {personal.location && <p>{personal.location}</p>}
          {personal.website && <p className="break-words">{personal.website}</p>}
          {personal.linkedin && <p className="break-words">{personal.linkedin}</p>}
        </div>
      </header>

      <main className="grid grid-cols-[65%_35%]">
        <div className="px-9 py-7">
          {personal.summary && (
            <section className="mb-6 border-l-4 border-amber-700 pl-4">
              <h2 className="font-bold uppercase tracking-[0.2em] text-amber-800" style={{ fontSize: `${0.75 * hScale}rem` }}>About</h2>
              <p className="mt-1 whitespace-pre-line text-stone-600" style={{ fontSize: `${0.6875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
            </section>
          )}

          {experience.length > 0 && (
            <section>
              <h2 className="mb-4 font-bold uppercase tracking-[0.2em] text-amber-800" style={{ fontSize: `${0.875 * hScale}rem` }}>Experience</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {experience.map((item) => (
                  <article key={item.id} className="grid grid-cols-[78px_1fr] gap-4">
                    <div className="border-r-2 border-orange-300 pr-3 text-right font-bold uppercase leading-4 text-amber-800" style={{ fontSize: `${0.6 * bScale}rem` }}>
                      <p>{formatDate(item.startDate, s.dateFormat)}</p>
                      <p>{item.current ? 'Present' : formatDate(item.endDate, s.dateFormat)}</p>
                    </div>
                    <div>
                      <h3 className="font-bold text-stone-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{item.title}</h3>
                      <p className="font-semibold text-amber-800" style={{ fontSize: `${0.65 * bScale}rem` }}>{item.company}{item.location && ` / ${item.location}`}</p>
                      {renderDescription(item.description, s.spacing.lineSpacing, bScale)}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {projects.length > 0 && (
            <section className="mt-6">
              <h2 className="mb-3 font-bold uppercase tracking-[0.2em] text-amber-800" style={{ fontSize: `${0.875 * hScale}rem` }}>Projects</h2>
              <div className="grid grid-cols-2 gap-3">
                {projects.map((project) => (
                  <article key={project.id} className="border-t-2 border-orange-300 pt-2">
                    <h3 className="font-bold text-stone-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{project.name}</h3>
                    {project.url && <p className="break-all text-amber-800" style={{ fontSize: `${0.55 * bScale}rem` }}>{project.url}</p>}
                    {project.description && <p className="mt-1 whitespace-pre-line text-stone-600" style={{ fontSize: `${0.6875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{project.description}</p>}
                    {project.technologies.length > 0 && <p className="mt-1 font-bold uppercase tracking-wide text-amber-800" style={{ fontSize: `${0.55 * bScale}rem` }}>{project.technologies.join(' / ')}</p>}
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="bg-orange-100 px-6 py-7">
          {skills.length > 0 && (
            <section>
              <h2 className="font-bold uppercase tracking-[0.2em] text-amber-800" style={{ fontSize: `${0.75 * hScale}rem` }}>Skills</h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {skills.map((skill) => <span key={skill.id} className="bg-amber-700 px-2 py-1 font-bold uppercase tracking-wide text-orange-50" style={{ fontSize: `${0.55 * bScale}rem` }}>{skill.name}</span>)}
              </div>
            </section>
          )}

          {education.length > 0 && (
            <section className="mt-7">
              <h2 className="font-bold uppercase tracking-[0.2em] text-amber-800" style={{ fontSize: `${0.75 * hScale}rem` }}>Education</h2>
              <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {education.map((item) => (
                  <article key={item.id} className="border-l-2 border-amber-700 pl-3">
                    <p className="font-bold uppercase text-amber-800" style={{ fontSize: `${0.55 * bScale}rem` }}>
                      {formatDate(item.startDate, s.dateFormat)} - {formatDate(item.endDate, s.dateFormat)}
                    </p>
                    <h3 className="mt-0.5 font-bold text-stone-900" style={{ fontSize: `${0.75 * hScale}rem` }}>{item.school}</h3>
                    <p className="text-stone-600" style={{ fontSize: `${0.6875 * bScale}rem` }}>{item.degree}{item.field && `, ${item.field}`}</p>
                    {item.location && <p className="text-stone-500" style={{ fontSize: `${0.6 * bScale}rem` }}>{item.location}</p>}
                    {item.description && <p className="mt-1 whitespace-pre-line text-stone-600" style={{ fontSize: `${0.6 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{item.description}</p>}
                  </article>
                ))}
              </div>
            </section>
          )}

          {certifications.length > 0 && (
            <section className="mt-7">
              <h2 className="font-bold uppercase tracking-[0.2em] text-amber-800" style={{ fontSize: `${0.75 * hScale}rem` }}>Certifications</h2>
              <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {certifications.map((certification) => (
                  <article key={certification.id}>
                    <h3 className="font-bold text-stone-900" style={{ fontSize: `${0.75 * hScale}rem` }}>{certification.name}</h3>
                    <p className="text-stone-600" style={{ fontSize: `${0.625 * bScale}rem` }}>{certification.issuer}{certification.date && ` / ${certification.date}`}</p>
                    {certification.url && <p className="break-all text-amber-800" style={{ fontSize: `${0.55 * bScale}rem` }}>{certification.url}</p>}
                  </article>
                ))}
              </div>
            </section>
          )}
        </aside>
      </main>
    </div>
  );
}
